// frontend/src/components/dashboard/pages/inventory/ingredients/components/IngredientStockAdjustmentDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Assessment,
  Save,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionDiv = motion.div;

const IngredientStockAdjustmentDialog = ({
  open,
  onClose,
  onSave,
  ingredient = null,
  loading = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    physical_count: '',
    reason: 'physical_count',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  // Adjustment reasons
  const adjustmentReasons = [
    { value: 'physical_count', label: 'Physical Count' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'expired', label: 'Expired' },
    { value: 'spoilage', label: 'Spoilage' },
    { value: 'theft', label: 'Theft/Loss' },
    { value: 'correction', label: 'System Correction' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (ingredient && open) {
      setFormData({
        physical_count: ingredient.current_stock?.toString() || '0',
        reason: 'physical_count',
        notes: ''
      });
    }
    setErrors({});
  }, [ingredient, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.physical_count || formData.physical_count === '') {
      newErrors.physical_count = t('inventory.validation.physicalCountRequired') || 'Physical count is required';
    } else if (parseFloat(formData.physical_count) < 0) {
      newErrors.physical_count = t('inventory.validation.physicalCountInvalid') || 'Physical count must be non-negative';
    }
    
    if (!formData.reason) {
      newErrors.reason = t('inventory.validation.reasonRequired') || 'Reason is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const submitData = {
      physical_count: parseFloat(formData.physical_count),
      reason: formData.reason,
      notes: formData.notes.trim() || null
    };
    
    onSave(submitData);
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  if (!ingredient) return null;

  const systemCount = parseFloat(ingredient.current_stock || 0);
  const physicalCount = parseFloat(formData.physical_count || 0);
  const variance = physicalCount - systemCount;
  const isIncrease = variance > 0;
  const isDecrease = variance < 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={loading}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white'
      }}>
        <Assessment />
        {t('inventory.ingredients.adjustStock') || 'Adjust Ingredient Stock'}
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Ingredient Info */}
          <Paper sx={{ p: 2, mb: 3,  mt: 4, bgcolor: 'primary.lighter' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('inventory.adjustingIngredient') || 'Adjusting Ingredient'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
              {ingredient.name}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Stock:</strong> {systemCount.toFixed(3)} {ingredient.unit_of_measure || 'units'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Min Stock:</strong> {parseFloat(ingredient.min_stock || 0).toFixed(3)}
              </Typography>
            </Stack>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('inventory.physicalCount') || 'Physical Count'}
                type="number"
                value={formData.physical_count}
                onChange={(e) => handleInputChange('physical_count', e.target.value)}
                error={!!errors.physical_count}
                helperText={errors.physical_count || `Current system count: ${systemCount.toFixed(3)} ${ingredient.unit_of_measure || 'units'}`}
                disabled={loading}
                required
                inputProps={{ min: 0, step: 0.001 }}
                autoFocus
              />
            </Grid>

            {formData.physical_count && !isNaN(physicalCount) && (
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: variance === 0 ? 'success.lighter' : (isIncrease ? 'info.lighter' : 'warning.lighter')
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {variance === 0 && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        No Variance - Stock Matches
                      </Typography>
                    )}
                    {isIncrease && (
                      <>
                        <TrendingUp color="info" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                          Stock Increase: +{Math.abs(variance).toFixed(3)} {ingredient.unit_of_measure || 'units'}
                        </Typography>
                      </>
                    )}
                    {isDecrease && (
                      <>
                        <TrendingDown color="warning" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                          Stock Decrease: -{Math.abs(variance).toFixed(3)} {ingredient.unit_of_measure || 'units'}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      System: {systemCount.toFixed(3)}
                    </Typography>
                    <Typography variant="body2">
                      Physical: {physicalCount.toFixed(3)}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      color: variance === 0 ? 'success.main' : (isIncrease ? 'info.main' : 'warning.main')
                    }}>
                      Variance: {variance > 0 ? '+' : ''}{variance.toFixed(3)}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.reason}>
                <InputLabel>{t('inventory.adjustmentReason') || 'Adjustment Reason'}</InputLabel>
                <Select
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  label={t('inventory.adjustmentReason') || 'Adjustment Reason'}
                  disabled={loading}
                  required
                >
                  {adjustmentReasons.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.reason && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.reason}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('inventory.notes') || 'Notes (Optional)'}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                disabled={loading}
                multiline
                rows={3}
                placeholder="Add any additional notes about this adjustment..."
              />
            </Grid>

            {Math.abs(variance) > (ingredient.min_stock || 0) && variance !== 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Large Variance Detected
                  </Typography>
                  <Typography variant="body2">
                    This adjustment represents a significant change in stock levels. 
                    Please ensure the physical count is accurate and provide detailed notes.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {Object.keys(errors).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {t('inventory.validation.pleaseFixErrors') || 'Please fix the errors above before submitting.'}
                </Alert>
              </Grid>
            )}
          </Grid>
        </MotionDiv>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || Object.keys(errors).length > 0 || variance === 0}
          startIcon={<Save />}
          sx={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            }
          }}
        >
          {loading 
            ? (t('common.adjusting') || 'Adjusting...') 
            : (t('inventory.adjustStock') || 'Adjust Stock')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IngredientStockAdjustmentDialog;